import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, EstadoVacio } from '@/components/Card';
import { TextField, NumberField, MoneyField, SelectField, DateField } from '@/components/Field';
import { IngredientForm } from '@/features/ingredients/IngredientForm';
import { buscarInsumos } from '@/db/repos/ingredients';
import { registrarCompra, proveedoresUsados } from '@/db/repos/purchases';
import { unidadesDisponibles, etiquetaUnidad, formatearCantidadBase } from '@/domain/units';
import { formatearCentavos, textoACentavos, type Centavos } from '@/domain/money';
import { hoy } from '@/domain/dates';
import { mensajeDeError } from '@/db/tx';
import type { Ingredient } from '@/db/types';

/** Renglon del ticket mientras se captura, antes de guardarse. */
interface RenglonBorrador {
  clave: string;
  insumo: Ingredient;
  cantidad: number;
  unidad: string;
  costoCentavos: Centavos;
}

type Panel = { modo: 'ticket' } | { modo: 'agregar' } | { modo: 'crearInsumo'; nombre: string };

/**
 * Captura de un ticket completo (Req. 6.1).
 *
 * La usuaria compra en supermercados y Costco: un ticket con varios productos.
 * La pantalla gira alrededor del ticket, no del producto, para no multiplicar
 * el trabajo de captura.
 */
export function NewPurchasePage() {
  const navegar = useNavigate();

  const [fecha, setFecha] = useState(hoy());
  const [proveedor, setProveedor] = useState('');
  const [renglones, setRenglones] = useState<RenglonBorrador[]>([]);
  const [panel, setPanel] = useState<Panel>({ modo: 'ticket' });

  const [proveedores, setProveedores] = useState<string[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advertencias, setAdvertencias] = useState<string[]>([]);

  useEffect(() => {
    let vigente = true;
    void proveedoresUsados().then((lista) => {
      if (vigente) setProveedores(lista);
    });
    return () => {
      vigente = false;
    };
  }, []);

  const total = renglones.reduce((suma, r) => suma + r.costoCentavos, 0);

  function agregarRenglon(renglon: Omit<RenglonBorrador, 'clave'>) {
    setRenglones((actuales) => [
      ...actuales,
      { ...renglon, clave: `${Date.now()}-${actuales.length}` },
    ]);
    setPanel({ modo: 'ticket' });
  }

  function quitarRenglon(clave: string) {
    setRenglones((actuales) => actuales.filter((r) => r.clave !== clave));
  }

  async function guardar() {
    setGuardando(true);
    setError(null);
    setAdvertencias([]);

    try {
      const resultado = await registrarCompra({
        fecha,
        proveedor: proveedor.trim() === '' ? null : proveedor,
        renglones: renglones.map((r) => ({
          ingredientId: r.insumo.id,
          cantidadCaptura: r.cantidad,
          unidadCaptura: r.unidad,
          costoCentavos: r.costoCentavos,
        })),
      });

      if (resultado.advertencias.length > 0) {
        setAdvertencias(resultado.advertencias);
        setGuardando(false);
        return;
      }

      navegar('/compras');
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  // ------------------------------------------------------------- Sub-paneles

  if (panel.modo === 'crearInsumo') {
    return (
      <div className="space-y-5">
        <PageHeader titulo="Nuevo insumo" descripcion="Para poder agregarlo al ticket" />
        <IngredientForm
          nombreInicial={panel.nombre}
          onGuardado={() => setPanel({ modo: 'agregar' })}
          onCancelar={() => setPanel({ modo: 'agregar' })}
        />
      </div>
    );
  }

  if (panel.modo === 'agregar') {
    return (
      <AgregarProducto
        onAgregar={agregarRenglon}
        onCrearInsumo={(nombre) => setPanel({ modo: 'crearInsumo', nombre })}
        onCancelar={() => setPanel({ modo: 'ticket' })}
      />
    );
  }

  // ---------------------------------------------------------------- El ticket

  return (
    <div className="space-y-5">
      <PageHeader titulo="Nueva compra" descripcion="Un ticket puede tener varios productos" />

      <DateField etiqueta="Fecha" valor={fecha} onCambio={setFecha} />

      <TextField
        etiqueta="Proveedor"
        valor={proveedor}
        onCambio={setProveedor}
        placeholder="Costco, Soriana, mercado…"
        lista={proveedores}
        ayuda={proveedores.length > 0 ? 'Puedes elegir uno de los que ya usaste' : undefined}
      />

      <section className="space-y-3">
        <h2 className="text-base font-semibold text-texto">
          Productos
          {renglones.length > 0 && (
            <span className="ml-2 font-normal text-texto-tenue">{renglones.length}</span>
          )}
        </h2>

        {renglones.length === 0 && (
          <EstadoVacio
            titulo="Sin productos todavía"
            descripcion="Agrega cada renglón del ticket."
          />
        )}

        {renglones.length > 0 && (
          <ul className="space-y-2">
            {renglones.map((r) => (
              <li key={r.clave}>
                <Card>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-texto">{r.insumo.nombre}</p>
                      <p className="text-sm text-texto-suave">
                        {r.cantidad} {etiquetaUnidad(r.unidad, r.cantidad)} ·{' '}
                        {formatearCentavos(r.costoCentavos)}
                      </p>
                      <p className="text-xs text-texto-tenue">
                        {formatearCentavos(Math.round(r.costoCentavos / r.cantidad))} por{' '}
                        {etiquetaUnidad(r.unidad, 1)}
                      </p>
                    </div>
                    <Button
                      variante="fantasma"
                      completo={false}
                      onClick={() => quitarRenglon(r.clave)}
                      aria-label={`Quitar ${r.insumo.nombre} del ticket`}
                    >
                      Quitar
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}

        <Button variante="secundario" onClick={() => setPanel({ modo: 'agregar' })}>
          + Agregar producto
        </Button>
      </section>

      {renglones.length > 0 && (
        <Card className="bg-superficie-suave">
          <div className="flex items-baseline justify-between">
            <span className="font-medium text-texto">Total del ticket</span>
            <span className="text-xl font-bold tabular-nums text-texto">
              {formatearCentavos(total)}
            </span>
          </div>
        </Card>
      )}

      {advertencias.length > 0 && (
        <Banner tono="aviso" titulo="La compra se guardó con avisos">
          <ul className="list-disc space-y-1 pl-4">
            {advertencias.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </Banner>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo guardar la compra">
          <p>{error}</p>
          <p className="mt-2 font-medium">No se registró nada a medias.</p>
        </Banner>
      )}

      <div className="space-y-2">
        <Button
          tamano="grande"
          disabled={guardando || renglones.length === 0}
          onClick={() => void guardar()}
        >
          {guardando ? 'Guardando…' : 'Guardar compra'}
        </Button>
        <Button variante="secundario" disabled={guardando} onClick={() => navegar('/compras')}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsAgregar {
  onAgregar: (renglon: Omit<RenglonBorrador, 'clave'>) => void;
  onCrearInsumo: (nombre: string) => void;
  onCancelar: () => void;
}

/** Un renglon del ticket: producto, cantidad, unidad y precio. */
function AgregarProducto({ onAgregar, onCrearInsumo, onCancelar }: PropsAgregar) {
  const [busqueda, setBusqueda] = useState('');
  const [encontrados, setEncontrados] = useState<Ingredient[]>([]);
  const [elegido, setElegido] = useState<Ingredient | null>(null);

  const [cantidad, setCantidad] = useState('');
  const [unidad, setUnidad] = useState('');
  const [precio, setPrecio] = useState('');

  useEffect(() => {
    let vigente = true;
    void buscarInsumos(busqueda).then((lista) => {
      if (vigente) setEncontrados(lista.slice(0, 8));
    });
    return () => {
      vigente = false;
    };
  }, [busqueda]);

  function elegir(insumo: Ingredient) {
    setElegido(insumo);
    setUnidad(insumo.unidadCompraPreferida);
    setBusqueda('');
  }

  const cantidadNumero = Number(cantidad);
  const centavos = textoACentavos(precio);
  const listo =
    elegido !== null &&
    Number.isFinite(cantidadNumero) &&
    cantidadNumero > 0 &&
    centavos !== null &&
    centavos >= 0;

  const opcionesUnidad =
    elegido === null
      ? []
      : unidadesDisponibles({
          dimension: elegido.dimension,
          factorEquivalencia: elegido.factorEquivalencia,
        }).map((u) => ({ valor: u, etiqueta: etiquetaUnidad(u, 2) }));

  const sinCoincidencias = busqueda.trim() !== '' && encontrados.length === 0;

  return (
    <div className="space-y-5">
      <PageHeader titulo="Agregar producto" descripcion="Un renglón del ticket" />

      {elegido === null ? (
        <>
          <TextField
            etiqueta="Producto"
            valor={busqueda}
            onCambio={setBusqueda}
            placeholder="Escribe para buscar…"
            autoFocus
          />

          {encontrados.length > 0 && (
            <ul className="space-y-1.5">
              {encontrados.map((insumo) => (
                <li key={insumo.id}>
                  <button
                    type="button"
                    onClick={() => elegir(insumo)}
                    className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-borde bg-superficie px-3 py-2.5 text-left active:bg-superficie-suave"
                  >
                    <span className="font-medium text-texto">{insumo.nombre}</span>
                    <span className="text-sm text-texto-suave">
                      {formatearCantidadBase(
                        insumo.stockBase,
                        insumo.dimension,
                        insumo.unidadCompraPreferida,
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {(sinCoincidencias || busqueda.trim() !== '') && (
            <Button variante="secundario" onClick={() => onCrearInsumo(busqueda.trim())}>
              + Crear «{busqueda.trim()}»
            </Button>
          )}

          {busqueda.trim() === '' && encontrados.length === 0 && (
            <EstadoVacio
              titulo="Todavía no hay insumos"
              descripcion="Crea el primero para poder agregarlo al ticket."
            >
              <Button variante="secundario" onClick={() => onCrearInsumo('')}>
                + Crear insumo
              </Button>
            </EstadoVacio>
          )}
        </>
      ) : (
        <>
          <Card>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-texto-tenue">Producto</p>
                <p className="font-semibold text-texto">{elegido.nombre}</p>
              </div>
              <Button variante="fantasma" completo={false} onClick={() => setElegido(null)}>
                Cambiar
              </Button>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <NumberField
              etiqueta="Cantidad"
              valor={cantidad}
              onCambio={setCantidad}
              placeholder="12"
              autoFocus
            />
            <SelectField
              etiqueta="Unidad"
              valor={unidad}
              opciones={opcionesUnidad}
              onCambio={setUnidad}
            />
          </div>

          <MoneyField
            etiqueta="Precio del renglón"
            valor={precio}
            onCambio={setPrecio}
            ayuda="Lo que dice el ticket por esa cantidad, no por unidad"
          />

          {listo && centavos !== null && (
            <Card className="bg-marca-suave">
              <p className="text-center text-sm text-texto-suave">Costo por unidad</p>
              <p className="text-center text-2xl font-bold tabular-nums text-marca-fuerte">
                {formatearCentavos(Math.round(centavos / cantidadNumero))}
                <span className="text-base font-normal text-texto-suave">
                  {' '}
                  / {etiquetaUnidad(unidad, 1)}
                </span>
              </p>
            </Card>
          )}

          <div className="space-y-2">
            <Button
              tamano="grande"
              disabled={!listo}
              onClick={() => {
                if (elegido === null || centavos === null) return;
                onAgregar({
                  insumo: elegido,
                  cantidad: cantidadNumero,
                  unidad,
                  costoCentavos: centavos,
                });
              }}
            >
              Agregar al ticket
            </Button>
            <Button variante="secundario" onClick={onCancelar}>
              Cancelar
            </Button>
          </div>
        </>
      )}

      {elegido === null && (
        <Button variante="fantasma" onClick={onCancelar}>
          Cancelar
        </Button>
      )}
    </div>
  );
}
